// FILE: src/components/AdminEditModal.js
// New component for the admin to edit restaurant details and re-assign owners.

import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Trash2 } from 'lucide-react'; // Added Trash2 to the import

export default function AdminEditModal({ restaurant, users, onClose, onSave, onDelete }) {
    const [editData, setEditData] = useState(restaurant);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        setEditData(restaurant);
    }, [restaurant]);

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');
        try {
            await onSave(editData);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setError('');
        try {
            await onDelete(restaurant.id);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to delete restaurant.');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full relative animate-fade-in-up">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><X size={28} /></button>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Edit Restaurant</h2>
                
                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Restaurant Name</label>
                        <input type="text" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} className="w-full p-2 border rounded-md"/>
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-700">Cuisine</label>
                        <input type="text" value={editData.cuisine} onChange={e => setEditData({...editData, cuisine: e.target.value})} className="w-full p-2 border rounded-md"/>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Address</label>
                        <input type="text" value={editData.address} onChange={e => setEditData({...editData, address: e.target.value})} className="w-full p-2 border rounded-md"/>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Assign Owner</label>
                        <select value={editData.ownerId} onChange={e => setEditData({...editData, ownerId: e.target.value})} className="w-full p-2 border rounded-md">
                            <option value="">-- Unassigned --</option>
                            {users.map(user => (
                                <option key={user.uid} value={user.uid}>{user.email} ({user.uid.substring(0, 6)}...)</option>
                            ))}
                        </select>
                    </div>

                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    <div className="flex justify-between items-center pt-4">
                        <button 
                            type="button" 
                            onClick={() => setShowDeleteConfirm(true)} 
                            className="text-red-600 font-semibold hover:text-red-800 flex items-center gap-2"
                        >
                            <Trash2 size={18}/> Delete Restaurant
                        </button>
                        <button type="submit" disabled={isSaving} className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2">
                            <Save size={18}/> {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>

                {showDeleteConfirm && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl p-8 shadow-2xl border text-center">
                            <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
                            <h3 className="text-xl font-bold mt-4">Are you sure?</h3>
                            <p className="text-gray-600 mt-2">This action cannot be undone.</p>
                            <div className="mt-6 flex justify-center gap-4">
                                <button onClick={() => setShowDeleteConfirm(false)} className="px-6 py-2 rounded-lg border">Cancel</button>
                                <button onClick={handleDelete} disabled={isDeleting} className="px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300">
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
